// Map-view filter state: URL search params are the source of truth so map
// links are shareable. One FilterState feeds the generated GraphQL filter
// objects below; enum members go up the wire UPPERCASE (HIKING, SUMMIT).
import type { Activity, PlaceType } from "./format";

export type FilterState = {
  activities: Activity[];
  placeTypes: PlaceType[];
  dateFrom?: string;
  dateTo?: string;
  elevMin?: number;
  elevMax?: number;
  /** Route distance bounds in km (the filter converts to meters for the API). */
  distMinKm?: number;
  distMaxKm?: number;
  /** Minimum route ascent in meters. */
  ascentMin?: number;
  q?: string;
  showRoutes: boolean;
  /** Only effective while showRoutes is on — tracks are a route sub-layer. */
  showTracks: boolean;
  showRegions: boolean;
  showOutings: boolean;
  showEvents: boolean;
  /** Cluster point pins at low zoom; off draws every pin individually. */
  clustering: boolean;
};

export const EMPTY_FILTERS: FilterState = {
  activities: [],
  placeTypes: [],
  showRoutes: true,
  showTracks: false,
  showRegions: false,
  showOutings: true,
  // Off by default per spec: events only appear when the layer is toggled on.
  showEvents: false,
  clustering: true,
};

export function filtersFromParams(params: URLSearchParams): FilterState {
  const list = (key: string) => params.get(key)?.split(",").filter(Boolean) ?? [];
  const num = (key: string) => {
    const v = Number(params.get(key));
    return Number.isFinite(v) && params.get(key) !== null ? v : undefined;
  };
  return {
    activities: list("act") as Activity[],
    placeTypes: list("pt") as PlaceType[],
    dateFrom: params.get("from") ?? undefined,
    dateTo: params.get("to") ?? undefined,
    elevMin: num("emin"),
    elevMax: num("emax"),
    distMinKm: num("dmin"),
    distMaxKm: num("dmax"),
    ascentMin: num("amin"),
    q: params.get("q") ?? undefined,
    showRoutes: params.get("routes") !== "0",
    showTracks: params.get("tracks") === "1",
    showRegions: params.get("regions") === "1",
    showOutings: params.get("outings") !== "0",
    showEvents: params.get("events") === "1",
    clustering: params.get("cluster") !== "0",
  };
}

export function filtersToParams(f: FilterState, params: URLSearchParams): void {
  const setOrDelete = (key: string, value: string | undefined) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };
  setOrDelete("act", f.activities.join(","));
  setOrDelete("pt", f.placeTypes.join(","));
  setOrDelete("from", f.dateFrom);
  setOrDelete("to", f.dateTo);
  setOrDelete("emin", f.elevMin?.toString());
  setOrDelete("emax", f.elevMax?.toString());
  setOrDelete("dmin", f.distMinKm?.toString());
  setOrDelete("dmax", f.distMaxKm?.toString());
  setOrDelete("amin", f.ascentMin?.toString());
  setOrDelete("q", f.q);
  setOrDelete("tracks", f.showTracks ? "1" : undefined);
  setOrDelete("regions", f.showRegions ? "1" : undefined);
  setOrDelete("events", f.showEvents ? "1" : undefined);
  if (f.showRoutes) params.delete("routes");
  else params.set("routes", "0");
  if (f.showOutings) params.delete("outings");
  else params.set("outings", "0");
  if (f.clustering) params.delete("cluster");
  else params.set("cluster", "0");
}

export function countActiveFilters(f: FilterState): number {
  return (
    f.activities.length +
    f.placeTypes.length +
    (f.dateFrom ? 1 : 0) +
    (f.dateTo ? 1 : 0) +
    (f.elevMin != null ? 1 : 0) +
    (f.elevMax != null ? 1 : 0) +
    (f.distMinKm != null ? 1 : 0) +
    (f.distMaxKm != null ? 1 : 0) +
    (f.ascentMin != null ? 1 : 0) +
    (f.q ? 1 : 0)
  );
}

/**
 * Viewport box in the shape pdbq's `BBoxInput` expects. Note the axis naming:
 * minX/minY are longitude/latitude, unlike the map_* functions' minLon/minLat.
 */
export type BBox = { minX: number; minY: number; maxX: number; maxY: number };

/**
 * PlaceFilter: bbox plus the type and elevation predicates that used to be
 * sieved client-side. Enum members go up the wire UPPERCASE.
 */
export function toPlaceFilter(f: FilterState, bbox: BBox): Record<string, unknown> {
  const filter: Record<string, unknown> = { geom: { bboxIntersects: bbox } };
  if (f.placeTypes.length) filter.type = { in: f.placeTypes };
  const elevation: Record<string, number> = {};
  if (f.elevMin != null) elevation.greaterThanOrEqualTo = f.elevMin;
  if (f.elevMax != null) elevation.lessThanOrEqualTo = f.elevMax;
  if (Object.keys(elevation).length) filter.elevation = elevation;
  return filter;
}

/**
 * RouteFilter: routes have no geometry column of their own, so the bbox test
 * reaches through the main route_track to the track's geom — the same join the
 * map_routes function did in SQL. `overlaps` is the array-column operator
 * (activities is an enum array), and `q` matches the old function's ILIKE.
 */
export function toRouteFilter(f: FilterState, bbox: BBox): Record<string, unknown> {
  const filter: Record<string, unknown> = {
    routeTracksByRouteId: {
      some: { role: { equalTo: "MAIN" }, track: { geom: { bboxIntersects: bbox } } },
    },
  };
  if (f.activities.length) filter.activities = { overlaps: f.activities };
  if (f.q) filter.name = { likeInsensitive: `%${f.q}%` };
  const distanceM: Record<string, number> = {};
  if (f.distMinKm != null) distanceM.greaterThanOrEqualTo = f.distMinKm * 1000;
  if (f.distMaxKm != null) distanceM.lessThanOrEqualTo = f.distMaxKm * 1000;
  if (Object.keys(distanceM).length) filter.distanceM = distanceM;
  if (f.ascentMin != null) filter.ascentM = { greaterThanOrEqualTo: f.ascentMin };
  return filter;
}

export function toRegionFilter(_f: FilterState, bbox: BBox): Record<string, unknown> {
  return { geom: { bboxIntersects: bbox } };
}

/**
 * OutingFilter: an outing is in view if its own track intersects the box, or
 * if a linked route's main track does — mirroring the map_outings function's
 * OR. Filtering only on `track` misses the route-linked ones entirely.
 */
export function toOutingMapFilter(f: FilterState, bbox: BBox): Record<string, unknown> {
  const inView = {
    or: [
      { track: { geom: { bboxIntersects: bbox } } },
      {
        outingRoutesByOutingId: {
          some: {
            route: {
              routeTracksByRouteId: {
                some: { role: { equalTo: "MAIN" }, track: { geom: { bboxIntersects: bbox } } },
              },
            },
          },
        },
      },
    ],
  };
  const rest = toOutingFilter(f);
  return rest ? { and: [inView, rest] } : inView;
}

/**
 * EventFilter for the map layer: only upcoming events that opted into map
 * display and have a meeting point in view. `now` is snapped down to the hour
 * so the serialized filter — which keys the layer cache — stays stable across
 * renders instead of forcing a refetch every time the filter is rebuilt.
 */
export function toEventMapFilter(_f: FilterState, bbox: BBox): Record<string, unknown> {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  return {
    location: { bboxIntersects: bbox },
    showOnMap: { equalTo: true },
    startsAt: { greaterThanOrEqualTo: now.toISOString() },
  };
}

/** GraphQL OutingFilter for the map/feed outing queries. */
export function toOutingFilter(f: FilterState): Record<string, unknown> | null {
  const filter: Record<string, unknown> = {};
  if (f.activities.length) filter.activity = { in: f.activities };
  const date: Record<string, string> = {};
  if (f.dateFrom) date.greaterThanOrEqualTo = f.dateFrom;
  if (f.dateTo) date.lessThanOrEqualTo = f.dateTo;
  if (Object.keys(date).length) filter.date = date;
  return Object.keys(filter).length ? filter : null;
}
